'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Flame, Search, ThumbsUp, ThumbsDown, Share2, Loader2, Globe, Filter } from 'lucide-react';
import { submissionService, RoastSubmission } from '@/lib/services/submissionService';
import { CATEGORIES } from '@/lib/roastData';
import { useLanguage } from '@/contexts/LanguageContext';
import { createClient } from '@/lib/supabase/client';

function Avatar({ text, size = 40 }: { text: string; size?: number }) {
  const colors = ['#22c55e', '#8b5cf6', '#f59e0b', '#06b6d4', '#ec4899', '#f97316'];
  const color = colors[text.charCodeAt(0) % colors.length];
  const initials = text.slice(0, 2).toUpperCase();
  return (
    <div className="rounded-full flex items-center justify-center font-bold text-white flex-shrink-0"
      style={{ width: size, height: size, background: color, fontSize: size * 0.35 }}>
      {initials}
    </div>
  );
}

function RoastCard({ sub, t }: { sub: RoastSubmission; t: (k: string) => string }) {
  const [upvotes, setUpvotes] = useState(sub.upvotes);
  const [downvotes, setDownvotes] = useState(sub.downvotes);
  const [voted, setVoted] = useState<'up' | 'down' | null>(null);
  const [pointsFlash, setPointsFlash] = useState(false);
  const [voteAnim, setVoteAnim] = useState<'up' | 'down' | null>(null);
  const cat = CATEGORIES.find(c => c.id === sub.category);

  // Sync when realtime updates come in
  useEffect(() => {
    setUpvotes(sub.upvotes);
    setDownvotes(sub.downvotes);
  }, [sub.upvotes, sub.downvotes]);

  const handleVote = async (dir: 'up' | 'down') => {
    if (voted === dir) {
      setVoted(null);
      if (dir === 'up') setUpvotes(sub.upvotes);
      else setDownvotes(sub.downvotes);
      return;
    }
    const wasUp = voted === 'up';
    const wasDown = voted === 'down';
    setVoted(dir);
    setVoteAnim(dir);
    setTimeout(() => setVoteAnim(null), 600);

    const supabase = createClient();
    if (dir === 'up') {
      const newUp = upvotes + 1;
      const newDown = wasDown ? downvotes - 1 : downvotes;
      setUpvotes(newUp);
      if (wasDown) setDownvotes(newDown);
      setPointsFlash(true);
      setTimeout(() => setPointsFlash(false), 1800);
      await supabase.from('roast_submissions').update({
        upvotes: newUp,
        downvotes: newDown,
        votes: newUp - newDown,
      }).eq('id', sub.id);
    } else {
      const newDown = downvotes + 1;
      const newUp = wasUp ? upvotes - 1 : upvotes;
      setDownvotes(newDown);
      if (wasUp) setUpvotes(newUp);
      await supabase.from('roast_submissions').update({
        downvotes: newDown,
        upvotes: newUp,
        votes: newUp - newDown,
      }).eq('id', sub.id);
    }
  };

  const dateStr = new Date(sub.submittedAt).toLocaleDateString('en-US', {
    day: 'numeric', month: 'short', year: 'numeric',
  });

  return (
    <div className="card-glass p-5 rounded-2xl transition-all duration-300 hover:scale-[1.01]"
      style={{ border: '1px solid var(--border)' }}>
      <div className="flex items-start gap-3 mb-3">
        <Avatar text={sub.country || 'RR'} size={40} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-sm" style={{ color: 'var(--foreground)' }}>
              🌍 {sub.country}
            </span>
            {cat && (
              <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                style={{ background: `${cat.color}18`, color: cat.color, border: `1px solid ${cat.color}30` }}>
                {cat.icon} {cat.label}
              </span>
            )}
          </div>
          <div className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
            {t('browse_submitted')}: {dateStr}
          </div>
        </div>
      </div>

      {sub.mode === 'text' && sub.roastText && (
        <p className="text-sm leading-relaxed mb-4 italic" style={{ color: 'var(--foreground)' }}>
          "{sub.roastText}"
        </p>
      )}
      {sub.mode === 'screenshot' && sub.screenshotUrl && (
        <div className="mb-4">
          <img src={sub.screenshotUrl} alt="Roast screenshot" className="rounded-xl max-w-full max-h-64 object-contain"
            style={{ border: '1px solid var(--border)' }} />
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <button onClick={() => handleVote('up')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95"
              style={{
                background: voted === 'up' ? 'rgba(34,197,94,0.2)' : 'rgba(255,255,255,0.05)',
                color: voted === 'up' ? 'var(--primary)' : 'var(--muted-foreground)',
                transform: voteAnim === 'up' ? 'scale(1.2)' : undefined,
                boxShadow: voted === 'up' ? '0 0 12px rgba(34,197,94,0.3)' : undefined,
              }}>
              <ThumbsUp size={14} />
              <span className="text-xs font-bold">{upvotes.toLocaleString()}</span>
            </button>
            {pointsFlash && (
              <span className="absolute -top-7 left-1/2 -translate-x-1/2 text-xs font-bold whitespace-nowrap px-2 py-1 rounded-full animate-bounce"
                style={{ color: '#fff', background: 'rgba(34,197,94,0.9)', boxShadow: '0 0 10px rgba(34,197,94,0.5)' }}>
                +10 pts! 🔥
              </span>
            )}
          </div>
          <button onClick={() => handleVote('down')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95"
            style={{
              background: voted === 'down' ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.05)',
              color: voted === 'down' ? '#ef4444' : 'var(--muted-foreground)',
              transform: voteAnim === 'down' ? 'scale(1.2)' : undefined,
            }}>
            <ThumbsDown size={14} />
            <span className="text-xs font-bold">{downvotes.toLocaleString()}</span>
          </button>
          {voted && (
            <span className="text-xs font-semibold px-2 py-1 rounded-lg"
              style={{ background: voted === 'up' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)', color: voted === 'up' ? 'var(--primary)' : '#ef4444' }}>
              {voted === 'up' ? '✓ Upvoted' : '✓ Downvoted'}
            </span>
          )}
        </div>
        <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all duration-200 hover:bg-white/5"
          style={{ color: 'var(--muted-foreground)' }}>
          <Share2 size={14} />
          <span className="text-xs">{t('rr_share')}</span>
        </button>
      </div>
    </div>
  );
}

export default function BrowseRoastsPage() {
  const { t } = useLanguage();
  const [submissions, setSubmissions] = useState<RoastSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [liveCount, setLiveCount] = useState(0);

  useEffect(() => {
    // Initial load — fetch only approved submissions for public voting
    submissionService.getApprovedSubmissions().then(data => {
      setSubmissions(data);
      setLiveCount(data.length);
      setLoading(false);
    });

    // Realtime subscription for live updates
    const supabase = createClient();
    const channel = supabase
      .channel('browse_roasts_realtime')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'roast_submissions',
      }, (payload) => {
        if (payload.eventType === 'INSERT') {
          // Don't show new submissions until approved
        } else if (payload.eventType === 'UPDATE') {
          const updated = mapPayloadToSubmission(payload.new);
          if (updated.status === 'approved') {
            // Add newly approved submissions to the feed
            setSubmissions(prev => {
              const exists = prev.some(s => s.id === updated.id);
              if (exists) return prev.map(s => s.id === updated.id ? updated : s);
              setLiveCount(c => c + 1);
              return [updated, ...prev];
            });
          } else {
            // Remove from public view if rejected or reverted to pending
            setSubmissions(prev => {
              const exists = prev.some(s => s.id === updated.id);
              if (exists) setLiveCount(c => Math.max(0, c - 1));
              return prev.filter(s => s.id !== updated.id);
            });
          }
        } else if (payload.eventType === 'DELETE') {
          setSubmissions(prev => prev.filter(s => s.id !== payload.old.id));
          setLiveCount(prev => Math.max(0, prev - 1));
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const filtered = submissions.filter(s => {
    const q = search.toLowerCase();
    const matchSearch = !q || s.roastText?.toLowerCase().includes(q) || s.country.toLowerCase().includes(q);
    const matchCat = categoryFilter === 'all' || s.category === categoryFilter;
    return matchSearch && matchCat;
  });

  return (
    <div className="min-h-screen pb-24 md:pb-8" style={{ background: 'var(--background)' }}>
      {/* Navbar */}
      <header className="sticky top-0 z-50 w-full" style={{ background: 'rgba(10,10,15,0.92)', backdropFilter: 'blur(20px)', borderBottom: '1px solid var(--border)' }}>
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link href="/roastrank" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #16a34a, #22c55e)' }}>
                <Flame size={18} className="text-white" />
              </div>
              <span className="font-bold text-lg" style={{ color: 'var(--foreground)' }}>Roast<span style={{ color: 'var(--primary)' }}>Rank</span></span>
            </Link>
            <nav className="hidden md:flex items-center gap-1">
              {[
                { label: t('rr_home'), href: '/roastrank' },
                { label: t('rr_leaderboard'), href: '/roastrank/leaderboard' },
                { label: t('rr_browse'), href: '/roastrank/browse' },
                { label: t('rr_submit'), href: '/roastrank/submit' },
              ].map(item => (
                <Link key={item.href} href={item.href}
                  className="px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 hover:bg-white/5"
                  style={{ color: item.href === '/roastrank/browse' ? 'var(--primary)' : 'var(--muted-foreground)' }}>
                  {item.label}
                </Link>
              ))}
            </nav>
            <Link href="/roastrank/submit"
              className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm transition-all duration-200 hover:scale-105"
              style={{ background: 'linear-gradient(135deg, #16a34a, #22c55e)', color: '#fff' }}>
              <Flame size={14} />
              {t('rr_submit_roast')}
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <Globe size={16} style={{ color: 'var(--primary)' }} />
            <span className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--primary)' }}>
              RoastRank
            </span>
            {/* Live indicator */}
            <span className="flex items-center gap-1.5 ml-2 px-2 py-0.5 rounded-full text-xs font-semibold"
              style={{ background: 'rgba(34,197,94,0.12)', color: 'var(--primary)', border: '1px solid rgba(34,197,94,0.25)' }}>
              <span className="pulse-dot" />
              LIVE
            </span>
          </div>
          <h1 className="text-3xl font-extrabold mb-2" style={{ color: 'var(--foreground)' }}>
            {t('browse_title')}
          </h1>
          <p style={{ color: 'var(--muted-foreground)' }}>
            {t('browse_desc')}
            {liveCount > 0 && (
              <span className="ml-2 font-semibold" style={{ color: 'var(--primary)' }}>
                · {liveCount} roast{liveCount !== 1 ? 's' : ''} live
              </span>
            )}
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('browse_search')}
              className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm outline-none"
              style={{ background: 'var(--card)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
            />
          </div>
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            <Filter size={14} style={{ color: 'var(--muted-foreground)', flexShrink: 0 }} />
            <button
              onClick={() => setCategoryFilter('all')}
              className="px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all duration-200"
              style={{
                background: categoryFilter === 'all' ? 'rgba(34,197,94,0.15)' : 'var(--card)',
                color: categoryFilter === 'all' ? 'var(--primary)' : 'var(--muted-foreground)',
                border: categoryFilter === 'all' ? '1px solid rgba(34,197,94,0.3)' : '1px solid var(--border)',
              }}>
              {t('browse_all')}
            </button>
            {CATEGORIES.map(cat => (
              <button key={cat.id} onClick={() => setCategoryFilter(cat.id)}
                className="px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all duration-200"
                style={{
                  background: categoryFilter === cat.id ? `${cat.color}18` : 'var(--card)',
                  color: categoryFilter === cat.id ? cat.color : 'var(--muted-foreground)',
                  border: categoryFilter === cat.id ? `1px solid ${cat.color}40` : '1px solid var(--border)',
                }}>
                {cat.icon} {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <Loader2 size={32} className="animate-spin" style={{ color: 'var(--primary)' }} />
            <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>{t('browse_loading')}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <Flame size={40} className="mx-auto mb-4 opacity-30" style={{ color: 'var(--muted-foreground)' }} />
            <p className="font-semibold" style={{ color: 'var(--foreground)' }}>{t('browse_no_results')}</p>
            <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>{t('browse_no_results_desc')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map(sub => (
              <RoastCard key={sub.id} sub={sub} t={t} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function mapPayloadToSubmission(row: any): RoastSubmission {
  return {
    id: row.id,
    mode: row.mode,
    roastText: row.roast_text,
    screenshotUrl: row.screenshot_url,
    screenshotPath: row.screenshot_path,
    category: row.category,
    country: row.country,
    context: row.context,
    status: row.status,
    submittedAt: row.submitted_at,
    reviewedAt: row.reviewed_at,
    adminNotes: row.admin_notes,
    votes: row.votes ?? 0,
    upvotes: row.upvotes ?? 0,
    downvotes: row.downvotes ?? 0,
  };
}
